import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, showMoney } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Shirt, MessageCircle } from "lucide-react";
import { ProposalDialog } from "@/components/advisor/ProposalDialog";
import { ProposalHistory } from "@/components/advisor/ProposalHistory";

const FIT = {
  ok: { label: "Perfetta", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" },
  limite: { label: "Al limite", cls: "border-amber-500/50 bg-amber-500/10 text-amber-400" },
};

const Pick = ({ p, checked, onToggle }) => (
  <label className={`flex cursor-pointer gap-3 rounded-md border p-2 transition-colors ${checked ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"} ${p.available ? "" : "opacity-60"}`}
    data-testid={`fit-pick-${p.model}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-1" data-testid={`fit-pick-check-${p.model}`} />
    {p.image_url ? <img src={p.image_url} alt={p.name} className="h-20 w-14 shrink-0 rounded object-cover" /> : <div className="h-20 w-14 shrink-0 rounded bg-muted" />}
    <div className="min-w-0 flex-1 text-sm">
      <p className="truncate font-medium">{p.name}</p>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="rounded bg-foreground px-2 py-0.5 font-mono-erp text-xs font-bold text-background" data-testid={`fit-pick-size-${p.model}`}>Taglia {p.size}</span>
        <Badge variant="outline" className={`text-[10px] ${FIT[p.status].cls}`}>{FIT[p.status].label}</Badge>
        {p.already_bought && <Badge variant="outline" className="text-[10px]">Già acquistato</Badge>}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {p.available ? `Disponibile in: ${p.colors.map((c) => `${c.color} (${num(c.qty)})`).join(", ")}` : `Esaurito nella taglia ${p.size}`}
      </p>
      {p.status === "limite" && p.issues[0] && <p className="text-[11px] text-amber-400">{p.issues.map((i) => i.measure).join(", ")} al limite{p.alt_size ? ` · valuta anche la ${p.alt_size}` : ""}</p>}
      <p className="text-[11px] text-muted-foreground">{showMoney() && `${eur(p.price)} · `}{num(p.sold)} venduti negli ultimi 90 giorni</p>
    </div>
  </label>
);

const Empty = ({ data }) => (
  <p className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="fit-picks-empty">
    {data.needs_measures ? "Inserisci le misure della cliente nel riquadro Misure del corpo: vedrai subito i capi che le stanno bene e la taglia giusta per ognuno."
      : data.no_charts ? "Nessun capo ha ancora la tabella misure. Compilala in Prodotti → Tabella misure per poter consigliare i capi."
        : "Nessun capo con tabella misure è adatto alle sue misure."}
  </p>
);

export const FitAdvisor = ({ customer }) => {
  const [sel, setSel] = useState({});
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["fit-picks", customer.id, customer.measurements?.updated_at],
    queryFn: async () => (await api.get(`/customers/${customer.id}/fit-picks`)).data });
  const items = data?.items || [];
  const chosen = items.filter((p) => sel[p.model]);
  return (
    <div className="rounded-md border border-primary/40 p-4" data-testid="fit-advisor">
      <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-primary"><Shirt className="h-3.5 w-3.5" />Capi che le stanno bene</p>
      <p className="mb-3 text-xs text-muted-foreground">Taglia calcolata dalle sue misure e dalla tabella misure di ogni capo. Spunta i capi da proporle e prepara il messaggio.</p>
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : !items.length ? <Empty data={data} /> : (
        <>
          <div className="space-y-2">
            {(all ? items : items.slice(0, 6)).map((p) => <Pick key={p.model} p={p} checked={!!sel[p.model]} onToggle={() => setSel((s) => ({ ...s, [p.model]: !s[p.model] }))} />)}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            {items.length > 6 ? <Button variant="ghost" size="sm" onClick={() => setAll(!all)} data-testid="fit-picks-toggle">{all ? "Mostra meno" : `Mostra tutti (${items.length})`}</Button> : <span />}
            <Button onClick={() => setOpen(true)} disabled={!chosen.length} data-testid="fit-prepare-message">
              <MessageCircle className="mr-2 h-4 w-4" />Prepara messaggio{chosen.length ? ` (${chosen.length})` : ""}
            </Button>
          </div>
        </>
      )}
      <ProposalHistory customerId={customer.id} />
      {open && <ProposalDialog customer={customer} items={chosen} onClose={(sent) => { setOpen(false); if (sent) setSel({}); }} />}
    </div>
  );
};
